import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../lib/api';
import { fmtHours, fmtDuration } from '../lib/formatters';
import { useAuth } from '../hooks/useAuth';
import EmployeeLink from '../components/EmployeeLink';
import { Clock, Users, Activity, TrendingUp, ExternalLink, Download, X } from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

function activityBadgeClass(pct) {
  if (pct >= 75) return 'bg-green-100 text-green-700';
  if (pct >= 40) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-600';
}

function activityBarColor(pct) {
  if (pct >= 75) return 'bg-green-500';
  if (pct >= 40) return 'bg-yellow-500';
  return 'bg-red-400';
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, loading, color = 'blue' }) {
  const colorMap = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${colorMap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-20 bg-gray-200 rounded animate-pulse" />
        ) : (
          <p className="mt-0.5 text-2xl font-bold text-gray-900">{value}</p>
        )}
        {sub && !loading && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Heatmap ─────────────────────────────────────────────────────────────────

function HeatmapCell({ value }) {
  const cls =
    value === 0  ? 'bg-gray-100' :
    value < 30   ? 'bg-blue-100' :
    value < 60   ? 'bg-blue-300' :
    value < 80   ? 'bg-blue-500' : 'bg-blue-700';
  return <div className={`w-6 h-6 rounded-sm ${cls} cursor-default`} title={`${value}%`} />;
}

function ActivityHeatmap({ data, dayLabels }) {
  if (!data || data.length === 0) {
    return <div className="flex items-center justify-center py-10 text-sm text-gray-400">No activity data for this week.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <div className="min-w-max">
        <div className="flex items-center gap-2 mb-2 ml-28">
          {dayLabels.map((d) => (
            <div key={d} className="w-6 text-center text-xs text-gray-400 font-medium">{d}</div>
          ))}
        </div>
        {data.map((row) => (
          <div key={row.name} className="flex items-center gap-2 mb-1.5">
            <div className="w-24 text-right text-xs text-gray-600 font-medium truncate pr-2">{row.name}</div>
            {row.days.map((val, i) => <HeatmapCell key={i} value={val} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Team Activity Board ──────────────────────────────────────────────────────

function TeamActivityBoard({ activeTrackers, allUsers, isLoading, navigate }) {
  const [tab, setTab] = useState('all');

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-2 w-48 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const trackerMap = Object.fromEntries((activeTrackers || []).map(t => [t.userId, t]));

  // Build unified employee list with status
  const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
  const employees = (allUsers || []).map(u => {
    const tracker = trackerMap[u.id];
    const isTracking = !!tracker;
    const activity = tracker?.activityLevel ?? tracker?.activity ?? null;
    // Idle = tracker running but no activity received in last 10 min
    const isIdle = isTracking && (!tracker.lastActiveAt || new Date(tracker.lastActiveAt).getTime() < tenMinutesAgo);
    const isOnline = u.isOnline || isTracking;
    return { ...u, tracker, isTracking, activity, isIdle, isOnline };
  });

  // Sort: tracking first (by activity desc), then online, then offline
  const sorted = [...employees].sort((a, b) => {
    if (a.isTracking && !b.isTracking) return -1;
    if (!a.isTracking && b.isTracking) return 1;
    if (a.isTracking && b.isTracking) return (b.activity ?? 0) - (a.activity ?? 0);
    if (a.isOnline && !b.isOnline) return -1;
    if (!a.isOnline && b.isOnline) return 1;
    return a.name.localeCompare(b.name);
  });

  const tabs = [
    { key: 'all',      label: 'All',      count: employees.length },
    { key: 'tracking', label: 'Tracking', count: employees.filter(e => e.isTracking && !e.isIdle).length },
    { key: 'idle',     label: 'Idle',     count: employees.filter(e => e.isIdle).length },
    { key: 'offline',  label: 'Offline',  count: employees.filter(e => !e.isTracking).length },
  ];

  const visible = sorted.filter(e => {
    if (tab === 'tracking') return e.isTracking && !e.isIdle;
    if (tab === 'idle')     return e.isIdle;
    if (tab === 'offline')  return !e.isTracking;
    return true;
  });

  // Comparison insights
  const trackingEmployees = employees.filter(e => e.isTracking && e.activity != null);
  const teamAvg = trackingEmployees.length > 0
    ? Math.round(trackingEmployees.reduce((s, e) => s + e.activity, 0) / trackingEmployees.length)
    : null;
  const mostActive  = trackingEmployees.sort((a, b) => b.activity - a.activity)[0];
  const leastActive = trackingEmployees.sort((a, b) => a.activity - b.activity)[0];

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              tab === t.key
                ? 'bg-gray-900 text-white'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
              tab === t.key ? 'bg-white bg-opacity-20 text-white' : 'bg-gray-100 text-gray-500'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Insight strip */}
      {teamAvg != null && (
        <div className="flex flex-wrap gap-4 mb-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
          <span>Team avg: <b className="text-gray-900">{teamAvg}%</b></span>
          {mostActive && <span>Most active: <b className="text-green-700">{mostActive.name.split(' ')[0]} ({Math.round(mostActive.activity)}%)</b></span>}
          {leastActive && leastActive.id !== mostActive?.id && (
            <span>Least active: <b className="text-red-600">{leastActive.name.split(' ')[0]} ({Math.round(leastActive.activity)}%)</b></span>
          )}
        </div>
      )}

      {/* Employee rows */}
      {visible.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-sm text-gray-400">No employees in this group.</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map((emp) => (
            <div
              key={emp.id}
              onClick={() => navigate(`/work-diary?userId=${emp.id}`)}
              className="flex items-center gap-3 py-3 px-1 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
            >
              {/* Avatar + status dot */}
              <div className="relative flex-shrink-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                  emp.isTracking ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {emp.name.charAt(0).toUpperCase()}
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                  emp.isTracking && !emp.isIdle ? 'bg-green-500' :
                  emp.isIdle ? 'bg-yellow-400' :
                  emp.isOnline ? 'bg-gray-400' : 'bg-gray-300'
                }`} />
              </div>

              {/* Name + project + tracking duration */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                  {emp.name}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {emp.isTracking
                    ? <>
                        {emp.tracker?.project || 'Tracking'}
                        {emp.tracker?.trackingFor > 0 && (
                          <span className="ml-1 text-gray-300">· {fmtDuration(emp.tracker.trackingFor)}</span>
                        )}
                      </>
                    : emp.isOnline ? 'Online' : 'Offline'}
                </p>
              </div>

              {/* Activity bar + % */}
              {emp.isTracking && emp.activity != null ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${activityBarColor(emp.activity)}`}
                      style={{ width: `${emp.activity}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${activityBadgeClass(emp.activity)}`}>
                    {Math.round(emp.activity)}%
                  </span>
                </div>
              ) : (
                <span className="text-xs text-gray-300 flex-shrink-0">—</span>
              )}

              {/* Hours today */}
              {emp.tracker?.hoursToday != null && (
                <span className="text-xs text-gray-500 w-14 text-right flex-shrink-0">
                  {fmtHours(emp.tracker.hoursToday)} today
                </span>
              )}

              <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-400 flex-shrink-0 transition-colors" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Download Banner ─────────────────────────────────────────────────────────

function DownloadBanner() {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
      <div className="flex items-center justify-center w-9 h-9 bg-blue-100 rounded-lg flex-shrink-0">
        <Download className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-blue-900">Install the {import.meta.env.VITE_APP_NAME || 'TrackFlow'} desktop agent</p>
        <p className="text-xs text-blue-700 mt-0.5">
          You need the desktop agent running to track time. Download it for your platform and sign in with your account credentials.
        </p>
      </div>
      <a
        href={import.meta.env.VITE_DOWNLOAD_URL || 'https://github.com/mnasir786/trackflow_haydevs/releases'}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex-shrink-0"
      >
        <Download className="w-3.5 h-3.5" />
        Download
      </a>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 text-blue-400 hover:text-blue-600 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const { isManager, user } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE';
  const isMasterAdmin = user?.isMasterAdmin;
  const viewingOrgAsAdmin = localStorage.getItem('viewingOrgAsAdmin');

  // Redirect Master Admins to org selector if not viewing specific org
  if (isMasterAdmin && !viewingOrgAsAdmin) {
    navigate('/org-selector', { replace: true });
    return null;
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get('/api/dashboard');
      return res.data;
    },
    refetchInterval: 30000,
    retry: false,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
    enabled: isManager,
    refetchInterval: 30000,
  });

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load dashboard data.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  const stats             = data?.stats || {};
  const weeklyHours       = data?.weeklyHours || [];
  const heatmap           = data?.heatmap || [];
  const heatmapDayLabels  = data?.heatmapDayLabels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const activeTrackers    = data?.activeTrackers || [];

  return (
    <div className="space-y-6">
      {/* Download banner — employees only */}
      {isEmployee && <DownloadBanner />}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Clock}     label="Today's Total Hours"  value={fmtHours(stats.todayHours)}          sub="across all members"    loading={isLoading} color="blue"   />
        <StatCard icon={Users}     label="Trackers Running"     value={stats.activeNow ?? 0}                   sub="currently tracking"    loading={isLoading} color="green"  />
        <StatCard icon={Activity}  label="Avg Activity %"       value={`${Math.round(stats.avgActivity ?? 0)}%`} sub="today"               loading={isLoading} color="purple" />
      </div>

      {/* Team Activity Board */}
      {isManager && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Team Activity</h2>
            <span className="text-xs text-gray-400">Live · refreshes every 30s</span>
          </div>
          <TeamActivityBoard
            activeTrackers={activeTrackers}
            allUsers={usersData}
            isLoading={isLoading}
            navigate={navigate}
          />
        </div>
      )}

      {/* Hours per member this week */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Hours Per Member — This Week</h2>
        {isLoading ? (
          <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
        ) : weeklyHours.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-sm text-gray-400">No logged hours this week.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={weeklyHours} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name"  tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis                 tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} unit="h" />
              <Tooltip formatter={(val) => [fmtHours(val), 'Hours']} contentStyle={{ borderRadius: '8px', fontSize: '13px' }} />
              <Bar
                dataKey="hours"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                onClick={(d) => {
                  if (d?.userId) navigate(`/work-diary?userId=${d.userId}`);
                }}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Activity Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Activity Heatmap — Last 7 Days</h2>
        {isLoading ? (
          <div className="h-32 bg-gray-100 rounded-lg animate-pulse" />
        ) : (
          <ActivityHeatmap data={heatmap} dayLabels={heatmapDayLabels} />
        )}
        <div className="flex items-center gap-2 mt-4">
          <span className="text-xs text-gray-400">Less</span>
          {['bg-gray-100', 'bg-blue-100', 'bg-blue-300', 'bg-blue-500', 'bg-blue-700'].map((c, i) => (
            <div key={i} className={`w-5 h-5 rounded-sm ${c}`} />
          ))}
          <span className="text-xs text-gray-400">More</span>
        </div>
      </div>
    </div>
  );
}
